import type { CSSProperties, RefObject, ReactElement } from 'react';
import { useMemo } from 'react';
import { Background, BackgroundVariant, MiniMap, ReactFlow, type Edge, type NodeMouseHandler, type OnMove, type ReactFlowInstance, } from '@xyflow/react';
import type { LayoutResult } from './layout-treemap';
import { TreemapBreadcrumb } from './TreemapBreadcrumb';
import { TreemapCellNode } from './TreemapCellNode';
import { FIT_VIEW_PADDING, MAX_ZOOM, MIN_ZOOM, TREEMAP_BACKGROUND_COLOR, } from './treemap-constants';
import { getTreemapMinimapNodeColor } from './treemap-minimap';
import type { ParsedTreemap, TreemapFlowNode } from './treemap-types';
const EMPTY_EDGES: Edge[] = [];
const PRO_OPTIONS = { hideAttribution: true } as const;
const FIT_VIEW_OPTIONS = { padding: FIT_VIEW_PADDING } as const;
// React Flow's stylesheet doesn't size `.react-flow`; the consumer must. The
// portal host is a flex column, so the wrapper grows to fill it (height:100%
// would resolve to 0 against the auto-height ancestor chain), and the canvas
// fills the wrapper absolutely.
const CANVAS_WRAPPER_STYLE: CSSProperties = {
    flex: 1,
    minHeight: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
};
const REACT_FLOW_STYLE: CSSProperties = { flex: 1, minHeight: 0, width: '100%' };
const MINIMAP_POSITION = 'bottom-right';
const MINIMAP_NODE_STROKE_COLOR = 'rgba(255, 255, 255, 0.55)';
const MINIMAP_MASK_COLOR = 'rgba(10, 14, 22, 0.22)';
const MINIMAP_STYLE: CSSProperties = {
    width: 162,
    height: 98,
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: 8,
    backgroundColor: 'rgba(10, 14, 22, 0.92)',
    boxShadow: 'none',
};
interface TreemapFlowCanvasProps {
    layout: LayoutResult;
    parsed: ParsedTreemap | null;
    drillPathIds: string[];
    isMinimapVisible: boolean;
    canvasWrapperRef: RefObject<HTMLDivElement | null>;
    onDrillToDepth: (index: number) => void;
    onReactFlowInit: (instance: ReactFlowInstance<TreemapFlowNode, Edge>) => void;
    onReactFlowMove: OnMove;
    onNodeClick: NodeMouseHandler<TreemapFlowNode>;
    onNodeMouseEnter: NodeMouseHandler<TreemapFlowNode>;
    onNodeMouseLeave: NodeMouseHandler<TreemapFlowNode>;
    onPaneClick: () => void;
}
export function TreemapFlowCanvas({ layout, parsed, drillPathIds, isMinimapVisible, canvasWrapperRef, onDrillToDepth, onReactFlowInit, onReactFlowMove, onNodeClick, onNodeMouseEnter, onNodeMouseLeave, onPaneClick, }: TreemapFlowCanvasProps): ReactElement {
    // Built here rather than at module scope, and memoised so React Flow still
    // sees one stable identity for the life of the canvas. The cell renders
    // markdown labels, which puts it on the markdown render cycle (label ->
    // Cmarkdown -> block renderer -> Cmarkdowntreemap -> this file); reading it
    // while the modules are still evaluating can hand back `undefined`. By
    // render time the graph has settled and it is defined.
    const nodeTypes = useMemo(() => ({ treemapCell: TreemapCellNode }), []);
    return (
    <div data-el="Cmarkdowntreemap/TreemapFlowCanvas/Root" ref={canvasWrapperRef} style={CANVAS_WRAPPER_STYLE}>
      <ReactFlow<TreemapFlowNode, Edge> nodes={layout.nodes} edges={EMPTY_EDGES} nodeTypes={nodeTypes} style={REACT_FLOW_STYLE} fitView fitViewOptions={FIT_VIEW_OPTIONS} nodesDraggable={false} nodesConnectable={false} nodesFocusable={false} edgesFocusable={false} elementsSelectable={false} panOnDrag panOnScroll zoomOnScroll zoomOnPinch zoomOnDoubleClick={false} minZoom={MIN_ZOOM} maxZoom={MAX_ZOOM} preventScrolling onInit={onReactFlowInit} onMove={onReactFlowMove} onNodeClick={onNodeClick} onNodeMouseEnter={onNodeMouseEnter} onNodeMouseLeave={onNodeMouseLeave} onPaneClick={onPaneClick} proOptions={PRO_OPTIONS}>
        <Background variant={BackgroundVariant.Dots} color={TREEMAP_BACKGROUND_COLOR} gap={16} size={1}/>
        <TreemapBreadcrumb parsed={parsed} drillPathIds={drillPathIds} onDrillToDepth={onDrillToDepth}/>
        {isMinimapVisible ? (<MiniMap position={MINIMAP_POSITION} nodeColor={getTreemapMinimapNodeColor} nodeStrokeColor={MINIMAP_NODE_STROKE_COLOR} nodeStrokeWidth={2} nodeBorderRadius={2} maskColor={MINIMAP_MASK_COLOR} style={MINIMAP_STYLE} pannable zoomable/>) : null}
      </ReactFlow>
    </div>
    );
}
